"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useT } from "@/Hooks";
import {
  HeaderSectionProps,
  HeroSectionProps,
} from "@/Templates/SectionsProps";
import { FC, ReactNode } from "react";
import HomeEighteenHeroSectionGenerated, {
  HomeEighteenHeroSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenHeroSection.generated";
import HomeFifteenHeroSectionGenerated, {
  HomeFifteenHeroSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenHeroSection.generated";
import HomeSeventeenHeroSectionGenerated, {
  HomeSeventeenHeroSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenHeroSection.generated";
import HomeSixteenHeroSectionGenerated, {
  HomeSixteenHeroSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenHeroSection.generated";
import {
  buildGeneratedSectionProps,
  GeneratedSectionRenderer,
  renderGeneratedSection,
} from "./generated-section-renderer";

type HeroAppearanceKey = ChildrenOf<"SiteTemplateHeroAppearance">;

const HeroAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateHeroAppearance;

function buildHeroHeaderSectionProps(
  props: HeroSectionProps,
): HeaderSectionProps {
  return {
    pageData: props.pageData,
    setting: {
      Appearance: props.setting.Appearance,
      HeroImage: props.setting.Image,
      HeroButtonDisplayStatus: props.setting.ButtonDisplayStatus,
      HeroButtonText: props.setting.ButtonText,
      HeroButtonLink: props.setting.ButtonLink,
      HeroVideoDisplayStatus: props.setting.ButtonDisplayStatus,
      HeroVideoImage: props.setting.Image,
      HeroVideoLink: { Id: -1, Name: "", Size: 0 },
    },
    lists: {
      HeroAvatars: [],
      HeroStatistics: [],
      SideImages: [],
    },
  };
}

function renderHtmlValue(value?: string | null): ReactNode | undefined {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getHeroImageUrl(props: HeroSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getHeroButtonVisible(
  setting: HeroSectionProps["setting"],
): boolean | undefined {
  if (
    setting.ButtonDisplayStatus === undefined ||
    setting.ButtonDisplayStatus === null
  ) {
    return undefined;
  }

  return (
    setting.ButtonDisplayStatus.Key === CenterOptions.Enum.Status.Active.key
  );
}

function getPrimaryPhone(props: HeroSectionProps): string | undefined {
  return (
    props.pageData.CommonData.Tels?.find((tel) => Boolean(tel)) ?? undefined
  );
}

function getPrimaryPhoneHref(props: HeroSectionProps): string | undefined {
  const phone = getPrimaryPhone(props);
  if (!phone) {
    return undefined;
  }

  return `tel:${phone}`;
}

function buildHomeFifteenSlides(
  defaults: HomeFifteenHeroSectionGeneratedProps["heroSectionSlides"],
  props: HeroSectionProps,
): HomeFifteenHeroSectionGeneratedProps["heroSectionSlides"] {
  const imageItems = props.lists.Default.filter((item) => item.Image?.Url);
  if (imageItems.length === 0) {
    return defaults;
  }

  return imageItems.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? defaults[0];

    return {
      ...baseItem,
      key: item.Id,
      src: item.Image?.Url ?? baseItem?.src ?? "",
      alt: item.Title ?? baseItem?.alt,
    };
  });
}

function buildHomeSixteenSlides(
  defaults: HomeSixteenHeroSectionGeneratedProps["heroSectionServicesSlides"],
  props: HeroSectionProps,
): HomeSixteenHeroSectionGeneratedProps["heroSectionServicesSlides"] {
  const titledItems = props.lists.Default.filter((item) => item.Title);
  if (titledItems.length === 0) {
    return defaults;
  }

  return titledItems.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? defaults[0];

    return {
      ...baseItem,
      key: item.Id,
      h20Text: item.Title ?? baseItem?.h20Text ?? "",
    };
  });
}

function buildHomeSeventeenCards(
  defaults: HomeSeventeenHeroSectionGeneratedProps["heroSectionBannerSeventeenFadeInDowns"],
  props: HeroSectionProps,
): HomeSeventeenHeroSectionGeneratedProps["heroSectionBannerSeventeenFadeInDowns"] {
  const mappedItems = props.lists.Default.filter(
    (item) => item.Title || item.Subtitle || item.Image?.Url,
  );
  if (mappedItems.length === 0) {
    return defaults;
  }

  return mappedItems.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? defaults[0];

    return {
      ...baseItem,
      key: item.Id,
      h20Text: item.Title ?? baseItem?.h20Text ?? "",
      span0Text: item.Subtitle ?? baseItem?.span0Text ?? "",
      src: item.Image?.Url ?? baseItem?.src ?? "",
    };
  });
}

function buildHomeEighteenSlides(
  defaults: HomeEighteenHeroSectionGeneratedProps["heroSectionServicesSlides"],
  props: HeroSectionProps,
): HomeEighteenHeroSectionGeneratedProps["heroSectionServicesSlides"] {
  const titledItems = props.lists.Default.filter((item) => item.Title);
  if (titledItems.length === 0) {
    return defaults;
  }

  return titledItems.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? defaults[0];

    return {
      ...baseItem,
      key: item.Id,
      h20Text: item.Title ?? baseItem?.h20Text ?? "",
    };
  });
}

function buildHomeEighteenCounters(
  defaults: HomeEighteenHeroSectionGeneratedProps["heroSectionCounterFadeInUps"],
  props: HeroSectionProps,
): HomeEighteenHeroSectionGeneratedProps["heroSectionCounterFadeInUps"] {
  const mappedItems = props.lists.Default.filter(
    (item) => item.Title || item.Subtitle || item.Description,
  );
  if (mappedItems.length === 0) {
    return defaults;
  }

  return mappedItems.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? defaults[0];

    return {
      ...baseItem,
      key: item.Id,
      p0Text: item.Title ?? baseItem?.p0Text ?? "",
      span0Text: item.Subtitle ?? item.Description ?? baseItem?.span0Text ?? "",
    };
  });
}

const generatedHeroRenderers = {
  [HeroAppearance.Home15.key]: (props: HeroSectionProps) => {
    const defaults =
      buildGeneratedSectionProps<HomeFifteenHeroSectionGeneratedProps>(
        "HomeFifteenHeroSection",
      );
    const imageUrl = getHeroImageUrl(props);
    const buttonVisible = getHeroButtonVisible(props.setting);
    const phone = getPrimaryPhone(props);
    const phoneHref = getPrimaryPhoneHref(props);

    return (
      <HomeFifteenHeroSectionGenerated
        {...defaults}
        menus={props.pageData.Menus}
        heroSectionSlides={buildHomeFifteenSlides(
          defaults.heroSectionSlides,
          props,
        )}
        divSection0Div0Div0Div0Div0H10H1Text={
          props.setting.BadgeText ??
          defaults.divSection0Div0Div0Div0Div0H10H1Text
        }
        divSection0Div0Div0Div0Div0H10Span0Span0SpanText={
          props.setting.Title ??
          defaults.divSection0Div0Div0Div0Div0H10Span0Span0SpanText
        }
        divSection0Div0Div0Div0Div0H10Span0SpanText={
          props.setting.Subtitle ??
          defaults.divSection0Div0Div0Div0Div0H10Span0SpanText
        }
        divSection0Div0Div0Div0Div0P0PText={
          renderHtmlValue(props.setting.Description) ??
          defaults.divSection0Div0Div0Div0Div0P0PText
        }
        divSection0Div0Div0Div0Div0Div0Link0LinkHref={
          props.setting.ButtonLink ??
          defaults.divSection0Div0Div0Div0Div0Div0Link0LinkHref
        }
        divSection0Div0Div0Div0Div0Div0Link0LinkText={
          props.setting.ButtonText ??
          defaults.divSection0Div0Div0Div0Div0Div0Link0LinkText
        }
        divSection0Div0Div0Div0Div0Div0Link0LinkVisible={
          buttonVisible ??
          defaults.divSection0Div0Div0Div0Div0Div0Link0LinkVisible
        }
        divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkHref={
          phoneHref ??
          defaults.divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkHref
        }
        divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkText={
          phone ?? defaults.divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkText
        }
        divImageWithBasePath0ImageSrc={
          imageUrl ?? defaults.divImageWithBasePath0ImageSrc
        }
      />
    );
  },
  [HeroAppearance.Home16.key]: (props: HeroSectionProps) => {
    const defaults =
      buildGeneratedSectionProps<HomeSixteenHeroSectionGeneratedProps>(
        "HomeSixteenHeroSection",
      );
    const imageUrl = getHeroImageUrl(props);

    return (
      <HomeSixteenHeroSectionGenerated
        {...defaults}
        headerSectionProps={buildHeroHeaderSectionProps(props)}
        heroSectionServicesSlides={buildHomeSixteenSlides(
          defaults.heroSectionServicesSlides,
          props,
        )}
        divDiv0Div0Div0H10H1Text={
          props.setting.Title ?? defaults.divDiv0Div0Div0H10H1Text
        }
        divDiv0Div0Div0H10Span0SpanText={
          props.setting.Subtitle ?? defaults.divDiv0Div0Div0H10Span0SpanText
        }
        divDiv0Div0Div0Div0Div2Div0P0PText={
          renderHtmlValue(props.setting.Description) ??
          defaults.divDiv0Div0Div0Div0Div2Div0P0PText
        }
        divDiv0Div0Div0Div0Div2Div0A0AHref={
          props.setting.ButtonLink ??
          defaults.divDiv0Div0Div0Div0Div2Div0A0AHref
        }
        divDiv0Div0Div0Div0Div2Div0A0Span0SpanText={
          props.setting.ButtonText ??
          defaults.divDiv0Div0Div0Div0Div2Div0A0Span0SpanText
        }
        divDiv0Div0Div0Div0Div0Div0H20H2Text={
          props.setting.BadgeText ??
          defaults.divDiv0Div0Div0Div0Div0Div0H20H2Text
        }
        divDiv0Div0Div0Div0Div0Div0P0PText={
          renderHtmlValue(props.setting.Description) ??
          defaults.divDiv0Div0Div0Div0Div0Div0P0PText
        }
        divDiv0ImageWithBasePath0ImageSrc={
          imageUrl ?? defaults.divDiv0ImageWithBasePath0ImageSrc
        }
        divImageWithBasePath0ImageSrc={
          imageUrl ?? defaults.divImageWithBasePath0ImageSrc
        }
      />
    );
  },
  [HeroAppearance.Home17.key]: (props: HeroSectionProps) => {
    const defaults =
      buildGeneratedSectionProps<HomeSeventeenHeroSectionGeneratedProps>(
        "HomeSeventeenHeroSection",
      );
    const imageUrl = getHeroImageUrl(props);

    return (
      <HomeSeventeenHeroSectionGenerated
        {...defaults}
        headerSectionProps={buildHeroHeaderSectionProps(props)}
        heroSectionBannerSeventeenFadeInDowns={buildHomeSeventeenCards(
          defaults.heroSectionBannerSeventeenFadeInDowns,
          props,
        )}
        divDiv0Div0Div0Div0Div0Div0DivText={
          props.setting.BadgeText ?? defaults.divDiv0Div0Div0Div0Div0Div0DivText
        }
        divDiv0Div0Div0Div0Div0H10Span0SpanText={
          props.setting.Title ??
          defaults.divDiv0Div0Div0Div0Div0H10Span0SpanText
        }
        divDiv0Div0Div0Div0Div0H10Span1SpanText={
          props.setting.Subtitle ??
          defaults.divDiv0Div0Div0Div0Div0H10Span1SpanText
        }
        divDiv0Div0Div0Div0Div0P0PText={
          renderHtmlValue(props.setting.Description) ??
          defaults.divDiv0Div0Div0Div0Div0P0PText
        }
        divDiv0Div0Div0Div0Div0Div1A0AHref={
          props.setting.ButtonLink ??
          defaults.divDiv0Div0Div0Div0Div0Div1A0AHref
        }
        divDiv0Div0Div0Div0Div0Div1A0Span0SpanText={
          props.setting.ButtonText ??
          defaults.divDiv0Div0Div0Div0Div0Div1A0Span0SpanText
        }
        divDiv0Div0Div0Div1Div0ImageWithBasePath0ImageSrc={
          imageUrl ?? defaults.divDiv0Div0Div0Div1Div0ImageWithBasePath0ImageSrc
        }
      />
    );
  },
  [HeroAppearance.Home18.key]: (props: HeroSectionProps) => {
    const defaults =
      buildGeneratedSectionProps<HomeEighteenHeroSectionGeneratedProps>(
        "HomeEighteenHeroSection",
      );
    const imageUrl = getHeroImageUrl(props);
    const phone = getPrimaryPhone(props);
    const phoneHref = getPrimaryPhoneHref(props);

    return (
      <HomeEighteenHeroSectionGenerated
        {...defaults}
        headerSectionProps={buildHeroHeaderSectionProps(props)}
        // heroSectionCounterFadeInUps={buildHomeEighteenCounters(defaults.heroSectionCounterFadeInUps, props)}
        heroSectionCounterFadeInUpsRenderer={() => <></>}
        heroSectionServicesSlides={buildHomeEighteenSlides(
          defaults.heroSectionServicesSlides,
          props,
        )}
        divDiv0Div0Div0Div0Div0H10Span0Span0SpanText={
          props.setting.BadgeText ??
          defaults.divDiv0Div0Div0Div0Div0H10Span0Span0SpanText
        }
        divDiv0Div0Div0Div0Div0H10Span1Span0SpanText={
          props.setting.Title ??
          defaults.divDiv0Div0Div0Div0Div0H10Span1Span0SpanText
        }
        divDiv0Div0Div0Div0Div0H10Span2Span0SpanText={
          props.setting.Subtitle ??
          defaults.divDiv0Div0Div0Div0Div0H10Span2Span0SpanText
        }
        divDiv0Div0Div0Div0Div0P0PText={
          renderHtmlValue(props.setting.Description) ??
          defaults.divDiv0Div0Div0Div0Div0P0PText
        }
        divDiv0Div0Div0Div0Div0Div0A0AHref={
          props.setting.ButtonLink ??
          defaults.divDiv0Div0Div0Div0Div0Div0A0AHref
        }
        divDiv0Div0Div0Div0Div0Div0A0AText={
          props.setting.ButtonText ??
          defaults.divDiv0Div0Div0Div0Div0Div0A0AText
        }
        divDiv0Div0Div0Div0Div0Div0Div0Div1A0AHref={
          phoneHref ?? defaults.divDiv0Div0Div0Div0Div0Div0Div0Div1A0AHref
        }
        divDiv0Div0Div0Div0Div0Div0Div0Div1A0AText={
          phone ?? defaults.divDiv0Div0Div0Div0Div0Div0Div0Div1A0AText
        }
        divDiv0Div0Div0Div1Div0Div0ImageWithBasePath0ImageSrc={
          imageUrl ??
          defaults.divDiv0Div0Div0Div1Div0Div0ImageWithBasePath0ImageSrc
        }
        divDiv0Div0Div0Div1Div0Div0ImageWithBasePath1ImageSrc={
          imageUrl ??
          defaults.divDiv0Div0Div0Div1Div0Div0ImageWithBasePath1ImageSrc
        }
      />
    );
  },
} satisfies Record<
  HeroAppearanceKey,
  GeneratedSectionRenderer<HeroSectionProps>
>;

const GeneratedHeroSection: FC<HeroSectionProps> = (props) => {
  const t = useT();

  return renderGeneratedSection(
    {
      ...props,
      setting: {
        ...props.setting,
        BadgeText: props.setting.BadgeText,
      },
    },
    {
      ...generatedHeroRenderers,
      [HeroAppearance.Home15.key]: (sectionProps: HeroSectionProps) => {
        const defaults =
          buildGeneratedSectionProps<HomeFifteenHeroSectionGeneratedProps>(
            "HomeFifteenHeroSection",
          );
        const imageUrl = getHeroImageUrl(sectionProps);
        const buttonVisible = getHeroButtonVisible(sectionProps.setting);
        const phone = getPrimaryPhone(sectionProps);
        const phoneHref = getPrimaryPhoneHref(sectionProps);

        return (
          <HomeFifteenHeroSectionGenerated
            {...defaults}
            menus={sectionProps.pageData.Menus}
            heroSectionSlides={buildHomeFifteenSlides(
              defaults.heroSectionSlides,
              sectionProps,
            )}
            divSection0Div0Div0Div0Div0H10H1Text={
              sectionProps.setting.BadgeText ??
              defaults.divSection0Div0Div0Div0Div0H10H1Text
            }
            divSection0Div0Div0Div0Div0H10Span0Span0SpanText={
              sectionProps.setting.Title ??
              defaults.divSection0Div0Div0Div0Div0H10Span0Span0SpanText
            }
            divSection0Div0Div0Div0Div0H10Span0SpanText={
              sectionProps.setting.Subtitle ??
              defaults.divSection0Div0Div0Div0Div0H10Span0SpanText
            }
            divSection0Div0Div0Div0Div0P0PText={
              renderHtmlValue(sectionProps.setting.Description) ??
              defaults.divSection0Div0Div0Div0Div0P0PText
            }
            divSection0Div0Div0Div0Div0Div0Link0LinkHref={
              sectionProps.setting.ButtonLink ??
              defaults.divSection0Div0Div0Div0Div0Div0Link0LinkHref
            }
            divSection0Div0Div0Div0Div0Div0Link0LinkText={
              sectionProps.setting.ButtonText ??
              defaults.divSection0Div0Div0Div0Div0Div0Link0LinkText
            }
            divSection0Div0Div0Div0Div0Div0Link0LinkVisible={
              buttonVisible ??
              defaults.divSection0Div0Div0Div0Div0Div0Link0LinkVisible
            }
            divSection0Div0Div0Div0Div0Div0Div0Div1P0PText={t(
              "landing.footer.contactUs",
              undefined,
              "تماس با ما",
            )}
            divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkHref={
              phoneHref ??
              defaults.divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkHref
            }
            divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkText={
              phone ??
              defaults.divSection0Div0Div0Div0Div0Div0Div0Div1Link0LinkText
            }
            divImageWithBasePath0ImageSrc={
              imageUrl ?? defaults.divImageWithBasePath0ImageSrc
            }
          />
        );
      },
      [HeroAppearance.Home18.key]: (sectionProps: HeroSectionProps) => {
        const defaults =
          buildGeneratedSectionProps<HomeEighteenHeroSectionGeneratedProps>(
            "HomeEighteenHeroSection",
          );
        const imageUrl = getHeroImageUrl(sectionProps);
        const phone = getPrimaryPhone(sectionProps);
        const phoneHref = getPrimaryPhoneHref(sectionProps);

        return (
          <HomeEighteenHeroSectionGenerated
            {...defaults}
            headerSectionProps={buildHeroHeaderSectionProps(sectionProps)}
            heroSectionServicesSlides={buildHomeEighteenSlides(
              defaults.heroSectionServicesSlides,
              sectionProps,
            )}
            divDiv0Div0Div0Div0Div0H10Span0Span0SpanText={
              sectionProps.setting.BadgeText ??
              defaults.divDiv0Div0Div0Div0Div0H10Span0Span0SpanText
            }
            divDiv0Div0Div0Div0Div0H10Span1Span0SpanText={
              sectionProps.setting.Title ??
              defaults.divDiv0Div0Div0Div0Div0H10Span1Span0SpanText
            }
            divDiv0Div0Div0Div0Div0H10Span2Span0SpanText={
              sectionProps.setting.Subtitle ??
              defaults.divDiv0Div0Div0Div0Div0H10Span2Span0SpanText
            }
            divDiv0Div0Div0Div0Div0P0PText={
              renderHtmlValue(sectionProps.setting.Description) ??
              defaults.divDiv0Div0Div0Div0Div0P0PText
            }
            divDiv0Div0Div0Div0Div0Div0A0AHref={
              sectionProps.setting.ButtonLink ??
              defaults.divDiv0Div0Div0Div0Div0Div0A0AHref
            }
            divDiv0Div0Div0Div0Div0Div0A0AText={
              sectionProps.setting.ButtonText ??
              defaults.divDiv0Div0Div0Div0Div0Div0A0AText
            }
            divDiv0Div0Div0Div0Div0Div0Div0Div1P0PText={t(
              "landing.footer.contactUs",
              undefined,
              "تماس با ما",
            )}
            divDiv0Div0Div0Div0Div0Div0Div0Div1A0AHref={
              phoneHref ?? defaults.divDiv0Div0Div0Div0Div0Div0Div0Div1A0AHref
            }
            divDiv0Div0Div0Div0Div0Div0Div0Div1A0AText={
              phone ?? defaults.divDiv0Div0Div0Div0Div0Div0Div0Div1A0AText
            }
            divDiv0Div0Div0Div1Div0Div0ImageWithBasePath0ImageSrc={
              imageUrl ??
              defaults.divDiv0Div0Div0Div1Div0Div0ImageWithBasePath0ImageSrc
            }
            divDiv0Div0Div0Div1Div0Div0ImageWithBasePath1ImageSrc={
              imageUrl ??
              defaults.divDiv0Div0Div0Div1Div0Div0ImageWithBasePath1ImageSrc
            }
          />
        );
      },
    },
    HeroAppearance.Home15.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedHeroSection;
